import { Plus, X } from 'lucide-react';
import { TextInput, SelectInput } from '../inputs';
import type { PartyEntry } from '../../types/invoice';

const PARTY_TYPE_OPTIONS = [
  { value: 'BT', label: 'Bill To' },
  { value: 'ST', label: 'Ship To' },
  { value: 'RE', label: 'Remit To' },
  { value: 'SF', label: 'Ship From' },
  { value: 'BY', label: 'Buying Party' },
  { value: 'SE', label: 'Selling Party' },
];

const ID_QUALIFIER_OPTIONS = [
  { value: '01', label: 'DUNS' },
  { value: '16', label: 'Zipcode' },
  { value: '92', label: 'Assigned by Buyer' },
  { value: '93', label: 'Assigned by Seller' },
];

interface InvoicePartiesSectionProps {
  parties: PartyEntry[];
  errors: Record<string, string>;
  onChange: (index: number, field: keyof PartyEntry, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

export function InvoicePartiesSection({
  parties,
  errors,
  onChange,
  onAdd,
  onRemove,
}: InvoicePartiesSectionProps) {
  return (
    <div className="card-compact p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Parties</h2>
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <Plus size={16} />
          Add Party
        </button>
      </div>

      {parties.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          No parties added. Click "Add Party" to add bill to, ship to, or remit to information.
        </p>
      )}

      <div className="space-y-6">
        {parties.map((party, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="absolute top-4 right-4 text-red-600 hover:text-red-700"
            >
              <X size={20} />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <SelectInput
                label="Party Type"
                value={party.type}
                onChange={(value) => onChange(index, 'type', value)}
                options={PARTY_TYPE_OPTIONS}
                error={errors[`party_${index}_type`]}
              />

              <TextInput
                label="Name"
                value={party.name}
                onChange={(value) => onChange(index, 'name', value)}
                error={errors[`party_${index}_name`]}
              />

              <SelectInput
                label="ID Qualifier"
                value={party.qualifier}
                onChange={(value) => onChange(index, 'qualifier', value)}
                options={ID_QUALIFIER_OPTIONS}
                error={errors[`party_${index}_qualifier`]}
              />

              <TextInput
                label="ID"
                value={party.id}
                onChange={(value) => onChange(index, 'id', value)}
                error={errors[`party_${index}_id`]}
              />

              <div className="md:col-span-2">
                <TextInput
                  label="Address Line 1"
                  value={party.address1 || ''}
                  onChange={(value) => onChange(index, 'address1', value)}
                  error={errors[`party_${index}_address1`]}
                />
              </div>

              <TextInput
                label="Address Line 2"
                value={party.address2 || ''}
                onChange={(value) => onChange(index, 'address2', value)}
                error={errors[`party_${index}_address2`]}
              />

              <TextInput
                label="City"
                value={party.city || ''}
                onChange={(value) => onChange(index, 'city', value)}
                error={errors[`party_${index}_city`]}
              />

              <TextInput
                label="State"
                value={party.state || ''}
                onChange={(value) => onChange(index, 'state', value)}
                error={errors[`party_${index}_state`]}
              />

              <TextInput
                label="Postal Code"
                value={party.postalCode || ''}
                onChange={(value) => onChange(index, 'postalCode', value)}
                error={errors[`party_${index}_postalCode`]}
              />

              <TextInput
                label="Country"
                value={party.country || ''}
                onChange={(value) => onChange(index, 'country', value)}
                error={errors[`party_${index}_country`]}
                placeholder="US"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
